import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@deliveryhero/pd-cookbook/components/ButtonV2';
import { Box, Collapse, Paper } from '@mui/material';

import ActionMenu from 'components/ActionMenu';
import CTA from 'components/CallToActions';
import Page, { ActionType } from 'components/Page';
import PageHeader from 'components/PageHeader/PageHeader';
import AllowanceIcon from 'components/svg/AllowanceIcon';
import FoodoraAllowance from 'components/svg/FoodoraAllowance';
import TableList from 'components/TableList';
import { Column } from 'components/TableList/SortableHeader';
import { RowsPerPage } from 'components/TableList/types';
import { format } from 'date-fns';
import { FormikProps } from 'formik';
import useAction from 'hooks/useAction';
import { ActionOptionType } from 'hooks/useAction/useAction';
import useApi from 'hooks/useApi';
import useDebounce from 'hooks/useDebounce';
import useNotification from 'hooks/useNotification';
import useQueryParam from 'hooks/useQueryParam/useQueryParam';
import useQueryWrapperP0 from 'hooks/useQueryWrapperP0';
import useQueryWrapperP1 from 'hooks/useQueryWrapperP1';
import useUser from 'hooks/useUser';
import { Allowance } from 'services/api';
import { dispatchGaEvent, PageType } from 'services/google-analytics';
import { AppRoute } from 'share/types/routes';
import { EmptyData } from 'src/components/svg/EmptyData';
import { useConfiguration } from 'src/contexts/country-config';
import { useFeatureFlags } from 'src/contexts/feature-flags';
import AllowanceQueryKeys from 'src/query-keys/allowance';
import DepartmentQueryKeys from 'src/query-keys/department';
import EmployeeQueryKeys from 'src/query-keys/employee';
import LocationQueryKeys from 'src/query-keys/location/location';
import { FeatureFlag } from 'src/utils/constants';
import isFoodora from 'src/utils/is-foodora/is-foodora';
import generateQuery from 'utils/generate-query';

import { AddAllowanceForm } from './AddAllowanceForm';
import { allowanceColumns } from './AllowanceColumns';
import { AllowanceValuesType } from './AllowanceDetailForm';
import { AllowanceFilters } from './AllowanceFilters';
import { AllowanceListingActionMenu } from './AllowanceListingActionMenu';
import { EditAllowanceForm } from './EditAllowanceForm';

const empty_page_title =
  'Create an allowance for employees to expense meals, groceries, and more';
const empty_page_subtitle =
  'You can set daily, weekly or monthly recurring allowances, or one-time allowances.';

const empty_search_title = 'No results found';
const empty_serach_subtitle = 'No records match the selected criteria';

export function AllowanceContainer() {
  const admin = useUser();
  const countryConfig = useConfiguration();
  const navigate = useNavigate();
  const api = useApi();
  const queryParam = useQueryParam();

  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [allowanceToDelete, setAllowanceToDelete] = useState<Allowance>();
  const { pushNotification, dismissAllNotifications, dismissNotification } =
    useNotification();
  const formRef = useRef<FormikProps<AllowanceValuesType>>(null);
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlags();

  useEffect(() => {
    dispatchGaEvent({ pageType: PageType.ALLOWANCE });
  }, []);

  // if allowance is enabled by the company
  const editable = admin.company.is_allowance_enabled;

  const page = queryParam.offset ? parseInt(queryParam.offset) : undefined;
  const rowsPerPage = queryParam.count ? parseInt(queryParam.count) : undefined;
  const tableListProps = {
    ...(page && {
      page: Math.floor(page / (rowsPerPage || RowsPerPage.TWENTYFIVE))
    }),
    ...(rowsPerPage && { rowsPerPage })
  };
  const filters = ['search', 'count', 'offset', 'state'];

  const filtered = Object.keys(queryParam)
    .filter((key) => filters.includes(key))
    .reduce((param: Record<string, string>, key) => {
      param[key] = queryParam[key];
      return param;
    }, {});

  const allowances = useQueryWrapperP0(AllowanceQueryKeys.list(filtered), () =>
    api.company.allowance.list(filtered)
  );

  const allAllowances = useQueryWrapperP0(AllowanceQueryKeys.list(), () =>
    api.company.allowance.list()
  );

  const [, setAction, options] = useAction<
    'add' | 'edit' | 'delete' | 'view'
  >();

  const handleClick = (
    action: 'add' | 'edit' | 'delete' | 'view',
    options?: ActionOptionType
  ) => {
    setAction(action, true, options);
  };

  const getLocations = useQueryWrapperP1(LocationQueryKeys.list(), () =>
    api.company.location.list({ count: 100 })
  );
  const locations = getLocations.data?.data ?? [];

  const getDepartments = useQueryWrapperP1(DepartmentQueryKeys.list(), () =>
    api.company.department.list({ count: 100 })
  );
  const departments = getDepartments.data?.data ?? [];

  const getAllowance = useQueryWrapperP1(
    AllowanceQueryKeys.detail(parseInt(options.id)),
    () => api.company.allowance.get(parseInt(options.id)),
    {
      enabled: !!options.id
    }
  );
  const allowance = getAllowance.data?.data;

  const debouncedSearchTerm = useDebounce(userSearchQuery, 500);

  const { data: users, isLoading: isUsersLoading } = useQueryWrapperP1(
    EmployeeQueryKeys.list({ name: debouncedSearchTerm }),
    () =>
      api.company.user.autocomplete(
        generateQuery({ search: debouncedSearchTerm })
      )
  );

  const deleteAllowance = useMutation(async (allowance_id: number) => {
    return api.company.allowance.delete(allowance_id);
  });

  const sendAllowanceEmail = useMutation(async (allowance_id: number) => {
    return api.company.allowance.notify(allowance_id);
  });

  const handleSendAllowanceEmail = useCallback(
    (allowance_id: Allowance['id']) => {
      sendAllowanceEmail.mutate(allowance_id, {
        onSuccess: () => {
          queryClient.invalidateQueries(AllowanceQueryKeys.list()).then(() =>
            pushNotification(
              `Allowance email sent on ${format(new Date(), 'MMM d, hh:mm a')}`,
              {
                variant: 'success'
              }
            )
          );
        },
        onError: () => {
          pushNotification(`We are unable to send allowance emails`, {
            variant: 'error'
          });
        }
      });
    },
    [pushNotification, queryClient, sendAllowanceEmail]
  );

  const currency = countryConfig.currencyCode;

  const columns: Column<Allowance>[] = useMemo(
    () =>
      allowanceColumns(
        currency,
        handleSendAllowanceEmail,
        featureFlags(FeatureFlag.AllowanceEmail)
      ),
    [currency, handleSendAllowanceEmail]
  );

  if (allowances.isError) {
    const error = allowances.error as Error;
    pushNotification(error.message, {
      variant: 'error',
      onClick: dismissAllNotifications
    });
  }

  const handlePageChange = (
    changes: Record<string, string | number | undefined>
  ) => {
    let params = {
      ...queryParam,
      ...changes
    };

    params = Object.keys(params).reduce((acc, key) => {
      params[key] !== undefined && (acc[key] = params[key]);
      return acc;
    }, {} as Record<string, any>);

    navigate(
      {
        pathname: AppRoute.Allowance,
        search: generateQuery(params)
      },
      { replace: true }
    );
  };

  const handlePaginationChange = (page: number, rowsPerPage: number) => {
    handlePageChange({
      ...{
        offset: page ? page * rowsPerPage : undefined,
        count: rowsPerPage
      }
    });
  };

  const data = allowances.data?.data || [];
  // to remove duplicated row
  const filtered_data = [
    ...data
      .reduce((a, c) => {
        a.set(c.id, c);
        return a;
      }, new Map())
      .values()
  ];

  const handleDelete = (allowance: Allowance) => {
    const deleteInProgressId = pushNotification(`Deleting allowance...`);
    deleteAllowance.mutate(allowance.id, {
      onSuccess: () => {
        queryClient.invalidateQueries(AllowanceQueryKeys.list()).then(() => {
          dismissNotification(deleteInProgressId);
          pushNotification(`Allowance deleted`, {
            variant: 'success'
          });
        });
      },
      onError: () => {
        dismissNotification(deleteInProgressId);
        pushNotification(`We are unable to delete the allowance`, {
          variant: 'error',
          onClick: () => handleDelete(allowance)
        });
      }
    });
  };

  return (
    <Page
      actions={{
        view: {
          component: (
            <EditAllowanceForm
              editable={false}
              currency={currency}
              formRef={formRef}
              setAction={setAction}
              allowance={allowance}
              departments={departments}
              users={users?.data || []}
              locations={locations}
              loadingAllowance={getAllowance.isLoading}
              loadingLocations={getLocations.isLoading}
              loadingDepartments={getDepartments.isLoading}
              loadingUsers={isUsersLoading}
              searchQuery={userSearchQuery}
              onQuery={(value: string) => setUserSearchQuery(value)}
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: 'Deleted allowance'
          }
        },
        edit: {
          component: (
            <EditAllowanceForm
              editable={editable}
              currency={currency}
              formRef={formRef}
              setAction={setAction}
              allowance={allowance}
              departments={departments}
              users={users?.data || []}
              locations={locations}
              loadingAllowance={getAllowance.isLoading}
              loadingLocations={getLocations.isLoading}
              loadingDepartments={getDepartments.isLoading}
              loadingUsers={isUsersLoading}
              searchQuery={userSearchQuery}
              onQuery={(value: string) => setUserSearchQuery(value)}
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: 'Edit an allowance',
            action: {
              label: 'Save',
              callback: () => {
                formRef.current?.handleSubmit();
              }
            }
          }
        },
        add: {
          component: (
            <AddAllowanceForm
              editable={editable}
              currency={currency}
              formRef={formRef}
              setAction={setAction}
              departments={departments}
              users={users?.data || []}
              locations={locations}
              loadingLocations={getLocations.isLoading}
              loadingDepartments={getDepartments.isLoading}
              loadingUsers={isUsersLoading}
              searchQuery={userSearchQuery}
              onQuery={(value: string) => setUserSearchQuery(value)}
            />
          ),
          type: ActionType.FullDialog,
          options: {
            title: 'Create an allowance',
            action: {
              label: 'Create',
              callback: () => {
                formRef.current?.handleSubmit();
              }
            }
          }
        },
        delete: {
          component: (
            <>
              <Button variant="secondary">Back to allowances</Button>
              <Button
                onClick={() => {
                  if (!allowanceToDelete) {
                    return;
                  }

                  handleDelete(allowanceToDelete as Allowance);
                }}
              >
                Delete
              </Button>
            </>
          ),
          type: ActionType.AppDialog,
          options: {
            title: `Delete an allowance`,
            content:
              'Once deleted, the allowance cannot be recovered and employees can no longer enjoy the corporate account benefits.'
          }
        }
      }}
    >
      <PageHeader
        title={'Allowance'}
        subtitle={<>Manage and update employee allowances.</>}
        buttons={[
          <Button
            onClick={() => {
              setUserSearchQuery('');
              handleClick('add');
            }}
            key="create-allowance"
          >
            Create an allowance
          </Button>
        ]}
      ></PageHeader>
      <TableList<Allowance>
        empty={
          <>
            {allAllowances.data?.data.length == 0 ? (
              <CTA
                illustration={isFoodora() ? FoodoraAllowance : AllowanceIcon}
                title={empty_page_title}
                subtitle={<>{empty_page_subtitle}</>}
              >
                <Button
                  onClick={() => {
                    setUserSearchQuery('');
                    handleClick('add');
                  }}
                >
                  Create an allowance
                </Button>
              </CTA>
            ) : (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    mb: 1,
                    py: 2,
                    px: 3
                  }}
                >
                  <Collapse in={true} collapsedSize={60}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flexDirection: 'column'
                      }}
                    >
                      <AllowanceFilters
                        params={queryParam}
                        onPageChange={handlePageChange}
                      />
                    </Box>
                  </Collapse>
                </Paper>
                <CTA
                  illustration={(props: React.SVGProps<SVGSVGElement>) => (
                    <EmptyData {...props} />
                  )}
                  title={empty_search_title}
                  subtitle={<>{empty_serach_subtitle}</>}
                >
                  <Button
                    onClick={() => {
                      handlePageChange({
                        search: undefined,
                        ['state']: undefined
                      });
                    }}
                  >
                    Clear all
                  </Button>
                </CTA>
              </>
            )}
          </>
        }
        loading={allowances.isLoading}
        columns={columns}
        data={filtered_data}
        onPaginationChange={handlePaginationChange}
        expandFilters={true}
        action={(allowance) => (
          <ActionMenu>
            {allowance.state == 'deleted' ? (
              <AllowanceListingActionMenu
                handleView={() => {
                  setAction('view', true, { id: allowance.id.toString() });
                }}
              />
            ) : (
              <AllowanceListingActionMenu
                handleEdit={() => {
                  setAction('edit', true, { id: allowance.id.toString() });
                }}
                handleDelete={() => {
                  setAllowanceToDelete(allowance);
                  setAction('delete');
                }}
              />
            )}
          </ActionMenu>
        )}
        total={
          allowances.error ? 0 : allowances.data ? allowances.data.total : -1
        }
        overflowXVisible={true}
        {...tableListProps}
      >
        <AllowanceFilters params={queryParam} onPageChange={handlePageChange} />
      </TableList>
    </Page>
  );
}
